import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AlertCircle, Plus, RefreshCcw } from 'lucide-react'
import Input from '../components/forms/Input'
import Modal from '../components/Modal'
import Select from '../components/forms/Select'
import Toast from '../components/Toast'
import PriorAuthCreateDrawer from '../components/priorAuth/PriorAuthCreateDrawer'
import PriorAuthDetailPanel from '../components/priorAuth/PriorAuthDetailPanel'
import PriorAuthRequestList from '../components/priorAuth/PriorAuthRequestList'
import {
  clinicalFormFromRow,
  emptyClinicalForm,
  type PriorAuthActivityItem,
  type PriorAuthClinicalForm,
  type PriorAuthDetailDocument,
  type PriorAuthDisplayRow,
} from '../components/priorAuth/priorAuthTypes'
import {
  apiToDisplayRow,
  clinicalFormToPatchPayload,
  demoDisplayRows,
  findSeedForRow,
  formatPaDate,
  normalizeStatus,
  normalizeStatusFromText,
} from '../components/priorAuth/priorAuthUtils'
import {
  defaultPriorAuthDocuments,
  normalizePriorAuthDocuments,
  priorAuthDocumentsToUploads,
} from '../components/priorAuth/priorAuthDocumentSlots'
import {
  hasValidationErrors,
  validatePriorAuthCreate,
} from '../components/priorAuth/priorAuthCreateValidation'
import { useTheme } from '../contexts/ThemeContext'
import {
  createPriorAuthorizationWithDocuments,
  getPriorAuthorization,
  listPriorAuthorizations,
  patchPriorAuthorization,
  patchPriorAuthorizationStatus,
  uploadPriorAuthorizationDocuments,
  PRIOR_AUTH_STATUS_OPTIONS,
  labelForPriorAuthStatus,
  type ApiPriorAuthorization,
  type ApiPriorAuthorizationActivity,
  type ApiPriorAuthorizationDocument,
  type CreatePriorAuthorizationPayload,
  type PatchPriorAuthorizationStatusPayload,
} from '../services/priorAuthorizationService'
import { generatePriorAuthRequestNumber } from '../data/patientData'
import { ROUTES, type PriorAuthCreatePrefill } from '../routes/routeMap'

type StatusFormState = PatchPriorAuthorizationStatusPayload

type PriorAuthRouteState = {
  openCreate?: boolean
  createStep?: number
  prefill?: PriorAuthCreatePrefill
} | null

const initialCreateFormState = (): CreatePriorAuthorizationPayload => {
  const today = new Date().toISOString().slice(0, 10)
  return {
    request_number: generatePriorAuthRequestNumber(),
    patient_id: '',
    payor_id: '',
    service_code: '',
    service_description: '',
    diagnosis_code: '',
    diagnosis_description: '',
    subscriber_id: '',
    requested_service_date: today,
    submitted_date: today,
    status: 'pending',
    provider_name: '',
    provider_npi: '',
    provider_address: '',
  }
}

const initialStatusFormState = (): StatusFormState => ({
  status: 'pending',
  status_label: 'Pending',
  description: '',
  performed_by_label: '',
})

const toSizeLabel = (value: unknown): string => {
  if (typeof value === 'number') {
    if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`
    return `${Math.round(value / 1024)} KB`
  }
  if (typeof value === 'string' && value.trim()) return value
  return ''
}

const PriorAuthorization = () => {
  const { theme } = useTheme()
  const location = useLocation()
  const navigate = useNavigate()
  const primaryColor = theme.colors.primary ?? '#0ea5e9'

  const [rows, setRows] = useState<PriorAuthDisplayRow[]>([])
  const [useDemoData, setUseDemoData] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [documents, setDocuments] = useState<PriorAuthDetailDocument[]>(() =>
    defaultPriorAuthDocuments()
  )
  const [activity, setActivity] = useState<PriorAuthActivityItem[]>([])
  const [clinical, setClinical] = useState<PriorAuthClinicalForm | null>(null)

  const [isLoadingList, setIsLoadingList] = useState(false)
  const [listLoadError, setListLoadError] = useState<string | null>(null)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [createInitialStep, setCreateInitialStep] = useState(0)
  const [isStatusOpen, setIsStatusOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const [isSubmittingPa, setIsSubmittingPa] = useState(false)
  const [createForm, setCreateForm] = useState(initialCreateFormState())
  const [statusForm, setStatusForm] = useState<StatusFormState>(initialStatusFormState())
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [showToast, setShowToast] = useState(false)
  const detailRequestRef = useRef(0)

  const selectedRow = useMemo(
    () => rows.find((r) => r.id === selectedId) ?? null,
    [rows, selectedId]
  )

  const showToastMsg = (message: string, type: 'success' | 'error' = 'success') => {
    setToastType(type)
    setToastMessage(message)
    setShowToast(true)
  }

  const showErrorToast = (error: unknown, fallback: string) => {
    showToastMsg(error instanceof Error ? error.message : fallback, 'error')
  }

  const loadSeedDetails = (row: PriorAuthDisplayRow) => {
    const seed = findSeedForRow(row.requestNumber, row.id)
    if (seed) {
      setDocuments(normalizePriorAuthDocuments(seed.documents))
      setActivity(seed.activity)
    } else {
      setDocuments(defaultPriorAuthDocuments())
      setActivity([])
    }
  }

  const mapApiDocuments = (items: ApiPriorAuthorizationDocument[]): PriorAuthDetailDocument[] =>
    items.map((item, index) => {
      const slotId =
        (typeof item.document_slot === 'string' && item.document_slot) ||
        (typeof item.id === 'string' && item.id) ||
        `doc-api-${index}`
      const fileName =
        (typeof item.file_name === 'string' && item.file_name) ||
        (typeof item.name === 'string' && item.name) ||
        'Document'
      const size =
        typeof item.file_size_bytes === 'number'
          ? item.file_size_bytes
          : item.size
      const uploadedBy =
        (typeof item.uploaded_by_label === 'string' && item.uploaded_by_label) ||
        (typeof item.uploaded_by === 'string' && item.uploaded_by) ||
        'System'

      return {
        id: slotId,
        name: fileName,
        meta: [
          toSizeLabel(size),
          formatPaDate(
            (typeof item.uploaded_at === 'string' && item.uploaded_at) ||
              (typeof item.created_at === 'string' && item.created_at) ||
              ''
          ),
          uploadedBy,
        ]
          .filter(Boolean)
          .join(' · '),
        required: false,
        uploaded: true,
        fileName,
        downloadUrl: typeof item.download_url === 'string' ? item.download_url : undefined,
      }
    })

  const mapApiActivity = (items: ApiPriorAuthorizationActivity[]): PriorAuthActivityItem[] =>
    items.map((item, index) => {
      const resolvedStatus =
        typeof item.status === 'string' && item.status.trim()
          ? normalizeStatus(item.status)
          : normalizeStatusFromText(item.status_label ?? item.action ?? item.event)
      const tone: PriorAuthActivityItem['tone'] =
        resolvedStatus === 'approved'
          ? 'green'
          : resolvedStatus === 'pending' || resolvedStatus === 'in_review'
            ? 'amber'
            : 'default'
      return {
        id: (typeof item.id === 'string' && item.id) || `act-${index}`,
        message:
          (typeof item.description === 'string' && item.description) ||
          (typeof item.details === 'string' && item.details) ||
          (typeof item.action === 'string' && item.action) ||
          (typeof item.event_type === 'string' && item.event_type.replace(/_/g, ' ')) ||
          'Activity update',
        time: formatPaDate(
          (typeof item.created_at === 'string' && item.created_at) ||
            (typeof item.timestamp === 'string' && item.timestamp) ||
            (typeof item.date === 'string' && item.date) ||
            ''
        ),
        tone,
      }
    })

  const clearDetailState = () => {
    setClinical(null)
    setDocuments(defaultPriorAuthDocuments())
    setActivity([])
  }

  const loadPriorAuthorizations = async () => {
    setIsLoadingList(true)
    const demo = demoDisplayRows()
    try {
      setListLoadError(null)
      const list = await listPriorAuthorizations()
      const apiMapped = list.map((item, i) => apiToDisplayRow(item as ApiPriorAuthorization, i))
      setUseDemoData(false)
      setRows(apiMapped)

      const nextId =
        selectedId && apiMapped.some((r) => r.id === selectedId)
          ? selectedId
          : apiMapped[0]?.id ?? null
      setSelectedId(nextId)

      if (nextId) {
        const row = apiMapped.find((r) => r.id === nextId)
        if (row) void loadPriorAuthDetails(nextId, row)
      } else {
        clearDetailState()
      }
    } catch (error) {
      setUseDemoData(true)
      setRows(demo)
      setListLoadError(
        error instanceof Error ? error.message : 'Failed to load prior authorizations from API.'
      )

      const nextId =
        selectedId && demo.some((r) => r.id === selectedId) ? selectedId : demo[0]?.id ?? null
      setSelectedId(nextId)

      if (nextId) {
        const row = demo.find((r) => r.id === nextId)
        if (row) void loadPriorAuthDetails(nextId, row)
      } else {
        clearDetailState()
      }
    } finally {
      setIsLoadingList(false)
    }
  }

  const applyDetailRow = (priorAuthId: string, detail: ApiPriorAuthorization): PriorAuthDisplayRow => {
    const resolvedId =
      (typeof detail.id === 'string' && detail.id) || priorAuthId
    const mapped = apiToDisplayRow({ ...detail, id: resolvedId }, 0)
    return { ...mapped, id: resolvedId }
  }

  const loadPriorAuthDetails = async (priorAuthId: string, row: PriorAuthDisplayRow) => {
    const requestId = ++detailRequestRef.current

    if (useDemoData || row.isDemo) {
      setClinical(clinicalFormFromRow(row))
      loadSeedDetails(row)
      return
    }

    setIsLoadingDetails(true)
    setClinical(clinicalFormFromRow(row))

    try {
      const { priorAuth, documents: docList, activity: activityList } =
        await getPriorAuthorization(priorAuthId)

      if (requestId !== detailRequestRef.current) return

      const detailRow = applyDetailRow(priorAuthId, priorAuth)
      setRows((prev) =>
        prev.map((entry) =>
          entry.id === priorAuthId || entry.requestNumber === detailRow.requestNumber
            ? detailRow
            : entry
        )
      )
      setSelectedId(detailRow.id)
      setClinical(clinicalFormFromRow(detailRow))
      setDocuments(normalizePriorAuthDocuments(mapApiDocuments(docList)))
      setActivity(mapApiActivity(activityList))
    } catch {
      if (requestId !== detailRequestRef.current) return
      setClinical(clinicalFormFromRow(row))
      loadSeedDetails(row)
    } finally {
      if (requestId === detailRequestRef.current) {
        setIsLoadingDetails(false)
      }
    }
  }

  useEffect(() => {
    loadPriorAuthorizations()
  }, [])

  useEffect(() => {
    const state = location.state as PriorAuthRouteState
    if (!state?.openCreate) return

    if (state.prefill) {
      setCreateForm((prev) => ({ ...prev, ...state.prefill }))
    }
    setCreateInitialStep(state.createStep ?? 1)
    setIsCreateOpen(true)
    navigate(ROUTES.PRIOR_AUTH, { replace: true, state: null })
  }, [location.state, navigate])

  const handleSelectRequest = useCallback(
    (priorAuthId: string) => {
      setSelectedId(priorAuthId)
      const row = rows.find((r) => r.id === priorAuthId)
      if (row) void loadPriorAuthDetails(priorAuthId, row)
    },
    [rows, useDemoData]
  )

  const handleCreateInput =
    (field: keyof CreatePriorAuthorizationPayload) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      setCreateForm((prev) => ({ ...prev, [field]: e.target.value }))
    }

  const handleCreateFormPatch = useCallback((patch: Partial<CreatePriorAuthorizationPayload>) => {
    setCreateForm((prev) => ({ ...prev, ...patch }))
  }, [])

  const openCreateDrawer = () => {
    setCreateForm(initialCreateFormState())
    setCreateInitialStep(0)
    setIsCreateOpen(true)
  }

  const handleCreate = async (e: FormEvent, createDocuments: PriorAuthDetailDocument[]) => {
    e.preventDefault()
    const { fieldErrors, documentErrors } = validatePriorAuthCreate(createForm, createDocuments)
    if (hasValidationErrors(fieldErrors) || hasValidationErrors(documentErrors)) {
      const firstFieldError = Object.values(fieldErrors).find(Boolean)
      const firstDocError = Object.values(documentErrors).find(Boolean)
      showToastMsg(
        firstFieldError || firstDocError || 'Complete all required fields before submitting.',
        'error'
      )
      return
    }
    const documentUploads = priorAuthDocumentsToUploads(createDocuments)
    if (documentUploads.length === 0) {
      showToastMsg('Upload required documents before submitting.', 'error')
      return
    }
    setIsCreating(true)
    try {
      const payload = {
        ...createForm,
        request_number: createForm.request_number.trim(),
        patient_id: createForm.patient_id.trim(),
        payor_id: createForm.payor_id.trim(),
        provider_name: createForm.provider_name.trim(),
        provider_npi: createForm.provider_npi.trim(),
        provider_address: createForm.provider_address.trim(),
      }
      await createPriorAuthorizationWithDocuments(payload, documentUploads)
      await loadPriorAuthorizations()
      setIsCreateOpen(false)
      setCreateForm(initialCreateFormState())
      showToastMsg('Prior authorization created successfully.')
    } catch (error) {
      showErrorToast(error, 'Unable to create prior authorization.')
    } finally {
      setIsCreating(false)
    }
  }

  const openStatusModal = () => {
    if (!selectedRow) return
    setStatusForm({
      status: selectedRow.status,
      status_label: selectedRow.statusLabel,
      description: '',
      performed_by_label: '',
    })
    setIsStatusOpen(true)
  }

  const handleStatusInput =
    (field: keyof StatusFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const value = e.target.value
      if (field === 'status') {
        const status = value as StatusFormState['status']
        setStatusForm((prev) => ({
          ...prev,
          status,
          status_label: labelForPriorAuthStatus(status),
        }))
        return
      }
      setStatusForm((prev) => ({ ...prev, [field]: value }))
    }

  const handleSubmitPa = async () => {
    if (!selectedRow) return
    const clinicalForm = clinical ?? clinicalFormFromRow(selectedRow)
    if (!clinicalForm.serviceCode.trim() || !clinicalForm.medicalNecessity.trim()) {
      showToastMsg('Complete required clinical fields before submitting.', 'error')
      return
    }

    if (useDemoData || selectedRow.isDemo) {
      showToastMsg('Prior authorization submitted (demo).')
      return
    }

    const priorAuthId = selectedRow.id
    const documentUploads = priorAuthDocumentsToUploads(documents)

    setIsSubmittingPa(true)
    try {
      await patchPriorAuthorization(priorAuthId, clinicalFormToPatchPayload(clinicalForm))
      if (documentUploads.length > 0) {
        await uploadPriorAuthorizationDocuments(priorAuthId, documentUploads)
      }
      await loadPriorAuthDetails(priorAuthId, selectedRow)
      showToastMsg('Prior authorization submitted successfully.')
    } catch (error) {
      showErrorToast(error, 'Unable to submit prior authorization.')
    } finally {
      setIsSubmittingPa(false)
    }
  }

  const handleStatusUpdate = async (e: FormEvent) => {
    e.preventDefault()
    if (!selectedRow) return
    if (!statusForm.status_label.trim() || !statusForm.performed_by_label.trim()) {
      showToastMsg('Status label and performed by are required.', 'error')
      return
    }
    if (useDemoData || selectedRow.isDemo) {
      setRows((prev) =>
        prev.map((r) =>
          r.id === selectedRow.id
            ? { ...r, status: statusForm.status, statusLabel: statusForm.status_label.trim() }
            : r
        )
      )
      setIsStatusOpen(false)
      showToastMsg('Status updated (demo).')
      return
    }
    setIsUpdatingStatus(true)
    try {
      const priorAuthId = selectedRow.id
      await patchPriorAuthorizationStatus(priorAuthId, {
        status: statusForm.status,
        status_label: statusForm.status_label.trim(),
        description: statusForm.description.trim(),
        performed_by_label: statusForm.performed_by_label.trim(),
      })
      setIsStatusOpen(false)
      await loadPriorAuthorizations()
      const refreshedRow = rows.find((r) => r.id === priorAuthId) ?? selectedRow
      await loadPriorAuthDetails(priorAuthId, refreshedRow)
      showToastMsg('Prior authorization status updated successfully.')
    } catch (error) {
      showErrorToast(error, 'Unable to update prior authorization status.')
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  return (
    <div
      className="flex flex-col w-full min-h-[calc(100vh-8rem)]"
      style={{ backgroundColor: '#f0f4f8' }}
    >
      <div className="bg-white border-b border-slate-200 px-6 sm:px-7 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between shrink-0">
        <div>
          <h1 className="text-xl sm:text-[22px] font-semibold text-slate-800">
            Prior Authorizations
          </h1>
          <p className="text-[13px] text-slate-500 mt-0.5">
            Manage DME request lifecycle from creation through payor decision.
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={loadPriorAuthorizations}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] font-medium border border-slate-200 rounded-lg bg-white text-slate-600 hover:bg-slate-50"
          >
            <RefreshCcw size={15} />
            Refresh
          </button>
          <button
            type="button"
            onClick={openCreateDrawer}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-[13px] font-semibold rounded-lg text-white"
            style={{ backgroundColor: primaryColor }}
          >
            <Plus size={14} strokeWidth={2.5} />
            Create Prior Auth
          </button>
        </div>
      </div>

      {listLoadError && (
        <div className="mx-6 sm:mx-7 mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>
            API load failed, showing demo prior authorizations. {listLoadError}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5 px-6 sm:px-7 py-5 flex-1 min-h-[28rem] lg:min-h-[calc(100vh-14rem)]">
        <PriorAuthRequestList
          rows={rows}
          selectedId={selectedId}
          isLoading={isLoadingList}
          primaryColor={primaryColor}
          onSelect={handleSelectRequest}
        />
        <PriorAuthDetailPanel
          row={selectedRow}
          clinical={clinical ?? (selectedRow ? clinicalFormFromRow(selectedRow) : emptyClinicalForm())}
          documents={documents}
          activity={activity}
          isLoadingDetails={isLoadingDetails}
          primaryColor={primaryColor}
          onClinicalChange={(patch) =>
            setClinical((prev) => ({ ...(prev ?? clinicalFormFromRow(selectedRow!)), ...patch }))
          }
          onDocumentsChange={setDocuments}
          onUpdateStatus={openStatusModal}
          onSaveDraft={() => showToastMsg('Draft saved.')}
          onSubmitPa={handleSubmitPa}
          isSubmittingPa={isSubmittingPa}
          onToast={showToastMsg}
        />
      </div>

      <PriorAuthCreateDrawer
        open={isCreateOpen}
        primaryColor={primaryColor}
        createForm={createForm}
        isCreating={isCreating}
        initialStep={createInitialStep}
        onClose={() => {
          setIsCreateOpen(false)
          setCreateInitialStep(0)
        }}
        onSubmit={handleCreate}
        onFormChange={handleCreateInput}
        onFormPatch={handleCreateFormPatch}
      />

      <Modal
        isOpen={isStatusOpen}
        onClose={() => setIsStatusOpen(false)}
        title="Update Prior Authorization Status"
        size="lg"
      >
        <form onSubmit={handleStatusUpdate} className="space-y-4">
          <Select
            label="Status"
            name="status"
            required
            value={statusForm.status}
            options={PRIOR_AUTH_STATUS_OPTIONS}
            onChange={handleStatusInput('status')}
          />
          <Input
            label="Status Label"
            name="status_label"
            required
            value={statusForm.status_label}
            onChange={handleStatusInput('status_label')}
          />
          <Input
            label="Performed By"
            name="performed_by_label"
            required
            value={statusForm.performed_by_label}
            onChange={handleStatusInput('performed_by_label')}
          />
          <div>
            <label
              className="mb-2 block text-sm font-medium"
              style={{ color: theme.colors.textSecondary }}
            >
              Description
            </label>
            <textarea
              name="description"
              rows={4}
              placeholder="Payor clinical review in progress."
              value={statusForm.description}
              onChange={handleStatusInput('description')}
              className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2"
              style={{
                backgroundColor: theme.colors.inputBackground,
                borderColor: theme.colors.border,
                color: theme.colors.textPrimary,
              }}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setIsStatusOpen(false)}
              className="px-4 py-2 text-sm font-medium border border-slate-200 rounded-lg bg-white text-slate-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUpdatingStatus}
              className="px-4 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-60"
              style={{ backgroundColor: primaryColor }}
            >
              {isUpdatingStatus ? 'Updating...' : 'Save Status'}
            </button>
          </div>
        </form>
      </Modal>

      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  )
}

export default PriorAuthorization
